import { useState } from "react";
import { Link } from "react-router-dom";
import { LayoutGrid, Clapperboard, Images, Inbox, Settings2, LogOut, ExternalLink, Instagram, Building2, MonitorPlay } from "lucide-react";
import SettingsTab from "@/components/admin/SettingsTab";
import DealersTab from "@/components/admin/DealersTab";
import ProductsTab from "@/components/admin/ProductsTab";
import VideosTab from "@/components/admin/VideosTab";
import WatchVideosTab from "@/components/admin/WatchVideosTab";
import GalleryTab from "@/components/admin/GalleryTab";
import EnquiriesTab from "@/components/admin/EnquiriesTab";
import InstagramTab from "@/components/admin/InstagramTab";

const TABS = [
  { key: "site", label: "Site & Hero", icon: Settings2 },
  { key: "dealers", label: "Dealers", icon: Building2 },
  { key: "products", label: "Products", icon: LayoutGrid },
  { key: "watch", label: "Watch Videos", icon: MonitorPlay },
  { key: "videos", label: "Review Videos", icon: Clapperboard },
  { key: "instagram", label: "Instagram", icon: Instagram },
  { key: "gallery", label: "Gallery", icon: Images },
  { key: "enquiries", label: "Enquiries", icon: Inbox },
];

export default function AdminPanel({ onLogout }) {
  const [tab, setTab] = useState("enquiries");
  const ActiveIcon = TABS.find((t) => t.key === tab)?.icon;

  return (
    <div data-testid="admin-panel" className="flex min-h-screen bg-bone">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col bg-ink text-white lg:flex">
        <div className="flex items-center gap-3 border-b border-white/10 px-6 py-5">
          <img src="/assets/logo-white.png" alt="SRS" className="h-9 w-9 object-contain" />
          <div className="leading-tight">
            <p className="font-display text-sm font-bold tracking-[0.14em]">SRS ADMIN</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">Sun Roofing Solutions</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 p-4">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm transition-colors ${
                tab === t.key ? "bg-srs text-white" : "text-white/60 hover:bg-white/5 hover:text-white"
              }`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="space-y-1 border-t border-white/10 p-4">
          <Link to="/" className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/50 hover:text-white">
            <ExternalLink size={15} /> View site
          </Link>
          <button
            data-testid="admin-logout-button"
            onClick={onLogout}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-white/50 hover:text-srs"
          >
            <LogOut size={15} /> Log out
          </button>
        </div>
      </aside>

      <div className="flex min-h-screen flex-1 flex-col lg:pl-60">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-line bg-white/90 px-5 py-4 backdrop-blur sm:px-8">
          <div className="flex items-center gap-3">
            {ActiveIcon && <ActiveIcon size={18} className="text-srs" />}
            <h1 className="font-display text-lg font-bold tracking-tight text-ink">
              {TABS.find((t) => t.key === tab)?.label}
            </h1>
          </div>
          <button data-testid="admin-logout-mobile" onClick={onLogout} className="font-mono text-[10px] uppercase tracking-[0.2em] text-subtle hover:text-srs lg:hidden">
            Log out
          </button>
        </header>

        <div className="flex gap-2 overflow-x-auto border-b border-line bg-white px-5 py-3 lg:hidden">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-mobile-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`shrink-0 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] ${
                tab === t.key ? "bg-ink text-white" : "text-mute"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <main className="flex-1 p-5 sm:p-8">
          {tab === "instagram" && <InstagramTab />}
          {tab === "site" && <SettingsTab />}
          {tab === "dealers" && <DealersTab />}
          {tab === "products" && <ProductsTab />}
          {tab === "watch" && <WatchVideosTab />}
          {tab === "videos" && <VideosTab />}
          {tab === "gallery" && <GalleryTab />}
          {tab === "enquiries" && <EnquiriesTab />}
        </main>
      </div>
    </div>
  );
}
